import { StyledReactSelect } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';

import { useStyles } from './styles';

export interface ConditionSelectOption {
  value: string;
  /**
   * Falls back to the value.
   *
   * A catalog field carries a label; an operator token and an `allowed_values`
   * entry do not always, and the stored token is a legible degradation where a
   * blank option is unpickable.
   */
  label?: string;
  /**
   * Listed but not pickable.
   *
   * The condition-type picker needs it: a type the server says cannot be
   * authored, or one a sibling row has already spent, has to stay visible — an
   * admin looking for pay gating needs to see it exists and read why — while
   * being unselectable. react-select honours `isDisabled` on an option itself,
   * so this only has to survive the mapping below.
   */
  isDisabled?: boolean;
}

export interface ConditionSelectProps {
  /** Lands on the input, so the row's controls stay individually addressable. */
  id: string;
  label: string;
  value: string;
  options: ConditionSelectOption[];
  onChange: (value: string) => void;
  /** The message for this control, once the admin has tried to save. */
  error?: string;
  isDisabled?: boolean;
  /** The control's place in the row, which the row decides rather than this. */
  className?: string;
}

/**
 * One picker in a condition row, on the design system's searchable select.
 *
 * Every list behind these is account-shaped: the field catalog is the account's
 * data keys minus the unsupported types, and a pick-list value is a data field's
 * own options. On the enterprise accounts this surface is built for that is
 * dozens to hundreds of entries, which a scroll-only menu handles worst of all —
 * so `isSearchable` is the point of the component and not a refinement.
 *
 * Configured the way `ComparatorSelect` and `DataKeySelect` in the workflow
 * editor configure the same components. Those two are not reused directly: both
 * mutate a workflow rule through `immer` and one reads the editor's context,
 * where a condition row here is driven by a draft and a callback. What carries
 * over is the configuration, not the wrapper.
 *
 * **On `StyledReactSelect` rather than the thinner `Dropdown` wrapper**, which
 * takes a string and looks the option up for you. Its lookup returns `undefined`
 * on a miss, and react-select reads an `undefined` `value` as *uncontrolled*:
 * `StateManager.getProp` falls back to `this.state.value`, which it writes on
 * every change and never from props. A cleared operand is exactly a miss — no
 * option has the value `''` — so the picker would go on displaying the choice
 * the admin made before the field changed under it, while the draft held
 * nothing. `null` is the only way to say "nothing is selected" to react-select,
 * and only a caller that resolves the option itself can pass it.
 *
 * Three deliberate departures from the two reference pickers:
 *
 * - `size="small"`. A row's other control is the typed value input, a 32px MUI
 *   input, and the design system's default `medium` select is 40px. One row
 *   holds both, and a row is not two densities.
 * - `tabSelectsValue={false}`. react-select otherwise commits the focused option
 *   on Tab, and opening a menu focuses the selected one — so looking at the
 *   field picker and tabbing away re-fires `onChange` with the field already
 *   chosen, and `withDataKey` clears the operator, value and compare key
 *   unconditionally. A MUI menu closes on Tab without selecting.
 * - No `required`. Every control in a row is required, so marking four of them
 *   per row with an asterisk says nothing the row does not already imply. What
 *   is missing is named on save instead, which is what `error` carries.
 */
export const ConditionSelect: React.FC<ConditionSelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  error,
  isDisabled,
  className,
}) => {
  const styles = useStyles();

  // Memoised on the caller's array: react-select compares `options` by identity
  // in `UNSAFE_componentWillReceiveProps` and takes a `setState` whenever it
  // changes, and the panel re-renders on every keystroke in the typed value
  // input beside this one.
  const resolved = React.useMemo(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label ?? option.value,
        isDisabled: option.isDisabled,
      })),
    [options],
  );

  return (
    <div
      className={clsx(
        styles.conditionSelect,
        className,
        isDisabled && styles.conditionSelectDisabled,
      )}
    >
      <StyledReactSelect
        inputId={id}
        label={label}
        // The design system sets `aria-labelledby` to the label *text* rather
        // than to an id, so it resolves to no element and the accessible name
        // falls through to `aria-label`. Passing both is what the workflow
        // editor's two pickers do, and it is the only thing naming the input —
        // several other selects in this app pass `label` alone and are unnamed.
        aria-label={label}
        options={resolved}
        // Explicitly `null` rather than a missing option, per the note above.
        value={resolved.find(option => option.value === value) ?? null}
        onChange={(option?: ConditionSelectOption | null) =>
          onChange(option?.value ?? '')
        }
        // Boolean, not the message: the design system renders a string `error`
        // as a caption positioned 24px below the control, which on a row this
        // dense lands on the compare-with checkbox underneath. The border still
        // turns, and the message is rendered in flow below.
        error={Boolean(error)}
        isDisabled={isDisabled}
        isSearchable
        tabSelectsValue={false}
        // Empty rather than the design system's "Please select...", which is
        // neither free of an ellipsis nor of the politeness the voice rules cut.
        // The label already names the control, so an unset picker needs nothing
        // in the box.
        placeholder=""
        size="small"
      />

      {/* `body2`, not `caption` or `subtitle1`: this theme leaves caption at
          14px unstyled and sets subtitle1 to 24px, so body2 is the size helper
          copy reads at here — the same variant the row's other explanatory line
          uses.

          `role="alert"` for the reason the scope group's message carries it: this
          is mounted only once a save has been refused, so its appearance is the
          event to announce, and a refused save moves no focus. It cannot be
          reached by `aria-describedby` instead — react-select builds the input's
          aria set from a fixed three (`aria-autocomplete`, `aria-label`,
          `aria-labelledby`) and drops everything else, so no attribute passed
          from here lands on the input. Announcing itself is what is available. */}
      {error && (
        <Typography
          className={styles.conditionSelectError}
          color="error"
          role="alert"
          variant="body2"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
